/**
 * The recipient wizard's step guard: which step the wizard may show for a
 * given `?step=` value, and what arriving there does to the entry.
 *
 * Pure so the whole transition table is unit-tested
 * (recipientStepGuard.test.ts); BeneficiaryProvider computes the effective
 * step from it during render and applies the side effects in a layout effect.
 */

import { createParser, parseAsStringEnum } from "nuqs";

export enum BeneficiaryFormStep {
  Destination = "destination",
  Account = "account",
  Details = "details",
}

/** Every step, in wizard order — also the nuqs enum parser's allow-list. */
export const BENEFICIARY_FORM_STEPS: BeneficiaryFormStep[] =
  Object.values(BeneficiaryFormStep);

/** What `?step=` holds when its value is not a step at all. */
export const UNRECOGNISED_STEP = "unrecognised";

/** `?step=` as read: a step, or a value that is not one. */
export type StepQuery = BeneficiaryFormStep | typeof UNRECOGNISED_STEP;

const stepEnumParser = parseAsStringEnum<BeneficiaryFormStep>(
  BENEFICIARY_FORM_STEPS
);

/**
 * The single parser for `?step=`. An absent param falls back to Destination;
 * a value that is not a step parses to UNRECOGNISED_STEP instead of null (null
 * would also fall back to the default), so the provider tells junk from "no
 * step" with one `useQueryState` call.
 */
export const stepQueryParser = createParser<StepQuery>({
  parse: (value) => stepEnumParser.parse(value) ?? UNRECOGNISED_STEP,
  serialize: (value) => value,
}).withDefault(BeneficiaryFormStep.Destination);

/** The step a `?step=` value asks for, and whether it was a step at all. */
export function readStepQuery(query: StepQuery): {
  requested: BeneficiaryFormStep;
  requestedIsValid: boolean;
} {
  return query === UNRECOGNISED_STEP
    ? { requested: BeneficiaryFormStep.Destination, requestedIsValid: false }
    : { requested: query, requestedIsValid: true };
}

/**
 * What the user has committed in this mount. Only the step handlers set them:
 * `destinationCommitted` by a Destination Continue that passed its check (the
 * fiat template check, or a resolved blockchain asset), `accountCommitted` by
 * a CoP modal handler or the blockchain address Continue. The URL alone can
 * never unlock a step.
 */
export type RecipientStepMarkers = {
  destinationCommitted: boolean;
  accountCommitted: boolean;
};

export const NO_STEP_MARKERS: RecipientStepMarkers = {
  destinationCommitted: false,
  accountCommitted: false,
};

/**
 * How the provider brings the URL back in line with the step it shows.
 * - `replace`: a fresh mount (refresh, deep link, bookmark, duplicated tab) or
 *   an unrecognised value; there is no wizard history to walk back through.
 * - `back`: a traversal inside this mount landed on a locked step. Every pushed
 *   Account entry sits on Destination and every Details entry on Account, so
 *   one step back is always the step being shown.
 */
export type RecipientStepCorrection = "none" | "replace" | "back";

export type RecipientStepTransition = {
  /** The step the wizard renders and the progress bar lights. */
  effectiveStep: BeneficiaryFormStep;
  /** Arrived at Destination from a later step: drop the whole entry. */
  restart: boolean;
  /** Arrived at Account: the CoP result and its commit no longer stand. */
  enterAccount: boolean;
  correction: RecipientStepCorrection;
};

/**
 * The requested step if its markers hold, otherwise the highest step that is
 * reachable — never above the one requested.
 */
export function resolveEffectiveStep(
  requested: BeneficiaryFormStep,
  markers: RecipientStepMarkers
): BeneficiaryFormStep {
  if (requested === BeneficiaryFormStep.Destination) {
    return BeneficiaryFormStep.Destination;
  }
  if (!markers.destinationCommitted) return BeneficiaryFormStep.Destination;
  if (requested === BeneficiaryFormStep.Details && markers.accountCommitted) {
    return BeneficiaryFormStep.Details;
  }
  return BeneficiaryFormStep.Account;
}

export function resolveRecipientStepTransition({
  requested,
  requestedIsValid,
  markers,
  previous,
}: {
  /** The step `?step` asks for (readStepQuery maps junk to Destination). */
  requested: BeneficiaryFormStep;
  /** False when the raw `?step` value is not a step at all. */
  requestedIsValid: boolean;
  markers: RecipientStepMarkers;
  /** The effective step last shown in this mount; null on a fresh mount. */
  previous: BeneficiaryFormStep | null;
}): RecipientStepTransition {
  const effectiveStep = resolveEffectiveStep(requested, markers);

  const restart =
    effectiveStep === BeneficiaryFormStep.Destination &&
    (previous === BeneficiaryFormStep.Account ||
      previous === BeneficiaryFormStep.Details);

  const enterAccount =
    effectiveStep === BeneficiaryFormStep.Account &&
    previous !== BeneficiaryFormStep.Account;

  const correction: RecipientStepCorrection = !requestedIsValid
    ? "replace"
    : requested === effectiveStep
      ? "none"
      : previous === null
        ? "replace"
        : "back";

  return { effectiveStep, restart, enterAccount, correction };
}
